/*
  Flowkeeper - Pomodoro timer for power users and teams
  Copyright (c) 2023 Constantine Kulak, Geoffrey Espagne

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU General Public License as published by
  the Free Software Foundation; either version 3 of the License, or
  (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU General Public License for more details.

  You should have received a copy of the GNU General Public License
  along with this program.  If not, see <https://www.gnu.org/licenses/>.
*/

import React, {useContext, useEffect, useRef, useState} from 'react';
import {
    ActionGroup,
    Bullseye,
    Button,
    ButtonType,
    ButtonVariant,
    DataList,
    Drawer,
    DrawerContent,
    DrawerContentBody,
    DrawerPanelContent,
    Form,
    FormContextProvider,
    Modal,
    ModalVariant,
    Page,
    TextInput,
    Toolbar,
    ToolbarContent,
    ToolbarItem,
} from '@patternfly/react-core';
import PlusIcon from '@patternfly/react-icons/dist/esm/icons/plus-icon';
import {executeAndSend} from "../../fk/core/websocket_event_source";
import {Root} from "../../fk/core/root";
import {CreateBacklog} from "../../fk/core/backlog";
import {v4 as uuid} from 'uuid';
import {Header} from '../misc/Header'
import {CreateWorkitem} from '../../fk/core/workitem';
import {OfflineBanner} from '../misc/OfflineBanner'
import {GenericAlert} from '../misc/GenericAlert';

import {BacklogDataListItem} from '../misc/BacklogDataListItem';
import {WorkItemDataListItem} from '../misc/WorkItemDataListItem';

import DataContext from '../../components/context/DataContext';
import ConnectionContext from '../context/ConnectionContext';
import {Connection} from '../../fk/core/connection';

function MainPage() {
    const ref = useRef<HTMLInputElement>(null);

    const { data, setData } = useContext(DataContext);
    const { connection } = useContext(ConnectionContext);

    const [isExpanded, setIsExpanded] = useState(false);

    const [selectedBacklogId, setSelectedBacklogId] = useState('');
    const [workitems, setWorkitems] = useState<any[]>([]);

    const [isCreateBacklogModalOpen, setIsCreateBacklogModalOpen] = useState(false);
    const [isCreateWorkitemModalOpen, setIsCreateWorkitemModalOpen] = useState(false);

    const createBacklog = (name: string) => {
        if (data) {
            const newBacklogId = uuid();
            executeAndSend(setData, CreateBacklog, newBacklogId, name);
            setSelectedBacklogId('');
        } else {
            throw new Error("Trying to create a backlog when there's no data");
        }
    }

    const createWorkitem = (name: string) => {
        if (data) {
            const newWorkitemId = uuid();
            executeAndSend(setData, CreateWorkitem, newWorkitemId, selectedBacklogId, name);
        } else {
            throw new Error("Trying to create a workitem when there's no data");
        }
    }

    useEffect(() => {
        if (data && data.children) {
            const backlog = data.children.get(selectedBacklogId);
            if (backlog) {
                setWorkitems(Array.from(backlog?.children.values()));
            } else {
                setIsExpanded(false);
            }
        }           
    }, [data, selectedBacklogId]);

    const createBacklogModal = () => {
        return (
            <Modal
                variant={ModalVariant.medium}
                title={"New backlog"}
                isOpen={isCreateBacklogModalOpen}
                onClose={handleCancelCreateBacklog}>
                <FormContextProvider initialValues={{'backlog-name': 'New backlog'}}>
                    {({setValue, getValue, setError}) => (
                        <Form>
                            <TextInput
                                id="backlog-name"
                                ref={ref}
                                onFocus={() => ref?.current?.select()}
                                onChange={(_event, value) => {
                                    setValue('backlog-name', value);
                                    setError('backlog-name', undefined);
                                }}
                                value={getValue('backlog-name')}/>
                            <ActionGroup>
                                <Button
                                    type={ButtonType.submit}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onAcceptCreateBacklog(getValue('backlog-name'));
                                    }}>Save</Button>
                                <Button
                                    type={ButtonType.button}
                                    variant={ButtonVariant.link}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleCancelCreateBacklog(e);
                                    }}>Cancel</Button>
                            </ActionGroup>
                        </Form>)}
                </FormContextProvider>
            </Modal>
        );
    }

    const handleCancelCreateBacklog = (_event: KeyboardEvent | React.MouseEvent) => {
        setIsCreateBacklogModalOpen(!isCreateBacklogModalOpen);
    };

    const onAcceptCreateBacklog = (name: string) => {
        createBacklog(name);
        setIsCreateBacklogModalOpen(!isCreateBacklogModalOpen);
    }

    const showCreateBacklogModal = () => {
        setIsCreateBacklogModalOpen(!isCreateBacklogModalOpen);
    }

    const createWorkitemModal = () => {
        return (
            <Modal
                variant={ModalVariant.medium}
                title={"New workitem"}
                isOpen={isCreateWorkitemModalOpen}
                onClose={handleCancelCreateWorkitem}>
                <FormContextProvider initialValues={{'workitem-name': 'Do something'}}>
                    {({setValue, getValue, setError}) => (
                        <Form>
                            <TextInput
                                id="workitem-name"
                                ref={ref}
                                onFocus={() => ref?.current?.select()}
                                onChange={(_event, value) => {
                                    setValue('workitem-name', value);
                                    setError('workitem-name', undefined);
                                }}
                                value={getValue('workitem-name')}/>
                            <ActionGroup>
                                <Button
                                    key="submit"
                                    type={ButtonType.submit}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onAcceptCreateWorkitem(getValue('workitem-name'));
                                    }}>Save</Button>
                                <Button
                                    key="cancel"
                                    type={ButtonType.button}
                                    variant={ButtonVariant.link}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleCancelCreateWorkitem(e);
                                    }}>Cancel</Button>
                            </ActionGroup>
                        </Form>)}
                </FormContextProvider>
            </Modal>
        );
    }

    const handleCancelCreateWorkitem = (_event: KeyboardEvent | React.MouseEvent) => {
        setIsCreateWorkitemModalOpen(!isCreateWorkitemModalOpen);
    };

    const onAcceptCreateWorkitem = (name: string) => {
        createWorkitem(name);
        setIsCreateWorkitemModalOpen(!isCreateWorkitemModalOpen);
    }

    const showCreateWorkitemModal = () => {
        setIsCreateWorkitemModalOpen(!isCreateWorkitemModalOpen);
    }

    const onSelectBacklog = (_event: React.KeyboardEvent | React.MouseEvent, id: string) => {
        if (data) {
            setIsExpanded(true);
            setSelectedBacklogId(id);
            let backlog = Array.from(data.children.values()).find(elt => elt.id == id);
            if (backlog) {
                let wkItems = Array.from(backlog.children.values());
                setWorkitems(wkItems);
            }
        }
    };

    const backlogList = (data: Root, connection: Connection) => {
        return (
            <>
                {createBacklogModal()}
                {createWorkitemModal()}
                <Toolbar id="toolbar-backlogs">
                    <ToolbarContent>
                        <ToolbarItem>
                            <Button
                                isDisabled={!connection || !connection.isConnected}
                                variant="primary"
                                icon={<PlusIcon />}
                                onClick={showCreateBacklogModal}>Add backlog</Button>
                        </ToolbarItem>
                    </ToolbarContent>
                </Toolbar>
                <DataList aria-label="backlog-list"
                    onSelectDataListItem={onSelectBacklog}
                    selectedDataListItemId={selectedBacklogId}>{
                        Array.from(data.children ? data.children.values() : [])
                            .sort((a: any, b: any) => (b.lastModifiedDate - a.lastModifiedDate))
                            .map(backlog => {
                                return <BacklogDataListItem
                                    id={backlog.id!}
                                    key={backlog.id!}
                                    name={backlog.name!}
                                    nbItems={backlog.children.size}
                                    setSelectedBacklogId={setSelectedBacklogId} />
                        })
                    }</DataList>
            </>
        )   
    }

    const workitemList = (data: Root, connection: Connection) => {
        return (
            <>
                <Toolbar id="toolbar-workitems">
                    <ToolbarContent>
                        <ToolbarItem>
                            <Button
                                key="cancel"
                                variant="link"
                                onClick={onCloseClick}>
                                &lt; Backlogs
                            </Button>
                        </ToolbarItem>
                        <ToolbarItem>
                            <Button
                                key="newWorkitem"
                                isDisabled={!connection.isConnected && selectedBacklogId != ''}
                                variant="primary"
                                icon={<PlusIcon />}
                                onClick={showCreateWorkitemModal}>Add item</Button>
                        </ToolbarItem>
                    </ToolbarContent>
                </Toolbar>
                <DataList aria-label="workitem-list">
                    {
                        workitems.map(workitem => {
                            const itemId = workitem.id;
                            const itemName = workitem.name;
                            const itemState = workitem.state;
                            return <WorkItemDataListItem id={itemId} key={itemId} name={itemName} state={itemState}/>
                        })
                    }
                </DataList>
            </>
        )   
    }

    const onCloseClick = () => {
        setIsExpanded(false);
        setSelectedBacklogId('');
    };

    const panelContent = (
        <DrawerPanelContent isResizable defaultSize={'65%'} minSize={'150px'}>
            {data && connection && workitemList(data, connection)}
        </DrawerPanelContent>
    );

    return (
        <Page
            mainContainerId='mainPage'
            header={Header()}
            >
            <OfflineBanner />
            {data && data.isDecryptError && <GenericAlert truncateTitle={2} message={'Error decrypting data. Please verify encryption key settings.'} />}
            {!data || !connection || !data.isLoggedIn ? (
                <Bullseye>
                    <img src="/offline.svg" width="20%" />
                </Bullseye>
            ) : (
                <Drawer isExpanded={isExpanded} isInline>
                    <DrawerContent panelContent={panelContent}>
                        <DrawerContentBody>{backlogList(data, connection)}</DrawerContentBody>
                    </DrawerContent>
                </Drawer>
            )}               
        </Page>
    );
}

export default MainPage;
